import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SettingsModal from '../../components/SettingsModal';
import { articleReadingConfig } from '../../config/settingsConfig';
import TRecorder from 'recorder-core';
import 'recorder-core/src/engine/mp3';
import 'recorder-core/src/engine/mp3-engine';
import { voiceAPI } from '../../services/api';
import './TextReading.css';

// 导入图片资源
import logoImage from '../../static/image/白字logo.png';
import listenIcon from '../../static/image/听.png';
import speakIcon from '../../static/image/说.png';
import scoreGoodImage from '../../static/image/测评好.png';
import scoreBadImage from '../../static/image/测评差.png';
import scoringImage from '../../static/image/测评中.png';
import { formatSentenceText } from '../../utils/formatText';

const TextReading = () => {
  const navigate = useNavigate();
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 文章分页状态
  const [currentArticleIndex, setCurrentArticleIndex] = useState(0);
  const [articles, setArticles] = useState([]);
  
  // 句子导航状态
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  
  // 显示控制
  const [showTranslation, setShowTranslation] = useState(true);
  
  // 播放状态管理
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false); // 默认暂停
  const isAutoPlayingRef = useRef(false);
  const isPlayingRef = useRef(false);
  const playbackIdRef = useRef(0);
  const navigationTimeoutRef = useRef(null);
  const prevArticleIndexRef = useRef(0); // 追踪上一次的文章索引
  
  // 全局音频管理
  const [currentAudio, setCurrentAudio] = useState(null);
  const [audioPlaybackCancelled, setAudioPlaybackCancelled] = useState(false);
  
  // 录音相关状态
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingPowerLevel, setRecordingPowerLevel] = useState(0);
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [showScoreImage, setShowScoreImage] = useState(false);
  const [scoreImageType, setScoreImageType] = useState('');
  const [currentEvaluatingSentence, setCurrentEvaluatingSentence] = useState(-1);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState(null); // 保存录音的 URL
  const recordedAudioUrlRef = useRef(null); // 使用 ref 保存 URL，确保能正确清理
  const recorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const screenClickHandlerRef = useRef(null);
  
  // 设置弹窗状态
  const [showReadingSettings, setShowReadingSettings] = useState(false);
  const [readingSettings, setReadingSettings] = useState(articleReadingConfig.defaultSettings);
  const readingSettingsRef = useRef(articleReadingConfig.defaultSettings);
  
  // 倒计时状态
  const [countdown, setCountdown] = useState(0); // 0表示未开始倒计时
  const countdownTimerRef = useRef(null);
  
  // 初始化录音器
  const initRecorder = () => {
    if (recorderRef.current) {
      return; // 已经初始化过了
    }
    
    recorderRef.current = TRecorder({
      type: "mp3",           // 录音格式：MP3
      bitRate: 128,          // 比特率：128kbps
      sampleRate: 44100,     // 采样率：44100Hz
      onProcess: function (buffers, powerLevel, duration, sampleRate) {
        setRecordingDuration(Math.floor(duration / 1000)); // 转换为秒
        setRecordingPowerLevel(powerLevel);
      }
    });
  };
  
  // 开始录音
  const startRecording = () => {
    if (isRecording) {
      return;
    }
    
    // 如果正在自动播放，先暂停自动播放
    if (isAutoPlaying) {
      setIsAutoPlaying(false);
      isAutoPlayingRef.current = false;
    }
    
    // 无论是否自动播放，只要有音频在播放就停止
    if (isPlaying || isPlayingRef.current || currentAudio) {
      stopCurrentAudio();
    }
    
    // 初始化录音器
    initRecorder();
    
    if (!recorderRef.current) {
      alert('录音器初始化失败');
      return;
    }
    
    try {
      const recorder = recorderRef.current;
      
      recorder.open(function () {
        console.log("录音器已打开，准备开始录音...");
        
        setIsRecording(true);
        setRecordingDuration(0);
        setRecordingPowerLevel(0);
        
        // 延迟开始录音，避免录到点击按钮的声音
        setTimeout(() => {
          if (recorderRef.current) {
            recorder.start();
            console.log("开始录音...");
          }
        }, 300);
        
        // 开始计时（注意：实际录音会延迟300ms开始）
        recordingTimerRef.current = setInterval(() => {
          setRecordingDuration(prev => prev + 1);
        }, 1000);
        
        // 添加屏幕点击监听器来结束录音
        const handleScreenClick = (e) => {
          // 点击任何地方或任何按钮都停止录音
          stopRecording();
        };
        
        screenClickHandlerRef.current = handleScreenClick;
        
        // 延迟添加监听器，避免立即触发
        setTimeout(() => {
          document.addEventListener('click', handleScreenClick);
        }, 400); // 增加延迟时间，确保录音已经开始
        
      }, function (msg, isUserNotAllow) {
        console.error("录音器打开失败：" + msg);
        
        let errorMessage = '录音器打开失败';
        if (isUserNotAllow) {
          errorMessage = '用户拒绝了录音权限，请在浏览器设置中允许录音权限';
        } else if (msg.includes('getUserMedia')) {
          errorMessage = '无法访问麦克风，请检查设备连接和权限设置';
        } else if (msg.includes('NotAllowedError')) {
          errorMessage = '录音权限被拒绝，请允许网站使用麦克风';
        } else if (msg.includes('NotFoundError')) {
          errorMessage = '未找到可用的录音设备，请检查麦克风连接';
        } else {
          errorMessage = '录音器打开失败：' + msg;
        }
        
        alert(errorMessage);
      });
      
    } catch (error) {
      console.error('启动录音失败:', error);
      alert('启动录音失败：' + error.message);
    }
  };
  
  // 停止录音并获取音频数据
  const stopRecording = async () => {
    if (!recorderRef.current || !isRecording) {
      return;
    }
    
    try {
      const recorder = recorderRef.current;
      
      // 立即移除点击监听器，避免重复触发
      if (screenClickHandlerRef.current) {
        document.removeEventListener('click', screenClickHandlerRef.current);
        screenClickHandlerRef.current = null;
      }
      
      recorder.stop(async function (blob, duration) {
        console.log("录音完成，时长：" + Math.floor(duration / 1000) + "秒");
        setIsRecording(false);
        
        // 清除计时器
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        
        // 检查录音时长
        const durationSeconds = Math.floor(duration / 1000);
        if (durationSeconds < 1) {
          alert('录音时间太短，请至少录制1秒钟');
          return;
        }
        
        if (durationSeconds > 60) {
          alert('录音时间过长，请录制60秒以内的音频');
          return;
        }
        
        // 检查音频数据
        if (!blob || blob.size === 0) {
          alert('录音数据为空，请重新录音');
          return;
        }
        
        // 直接使用 TRecorder 生成的 MP3 blob，不进行格式转换
        // 注意：TRecorder 已经配置为 MP3 格式和 44100Hz 采样率
        // 不进行裁剪，以保持原始的 MP3 格式和采样率
        
        // 获取当前句子进行评测
        const currentArticle = articles[currentArticleIndex];
        if (currentArticle && currentArticle.sentences && currentArticle.sentences[currentEvaluatingSentence]) {
          const currentSentence = currentArticle.sentences[currentEvaluatingSentence];
          if (currentSentence && currentSentence.title) {
            evaluateVoice(blob, currentSentence.title);
          } else {
            alert('当前句子数据异常，无法进行评测');
          }
        } else {
          alert('当前句子数据异常，无法进行评测');
        }
        
      }, function (msg) {
        console.error("录音停止失败：" + msg);
        setIsRecording(false);
        
        // 清除计时器
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        
        alert('录音停止失败：' + msg);
      });
      
      recorderRef.current = null;
      
    } catch (error) {
      console.error('停止录音异常:', error);
      setIsRecording(false);
      
      // 清除计时器
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      
      alert('停止录音失败：' + error.message);
    }
  };
  
  // 语音评测 API 调用（创建音频 URL 并先播放）
  const evaluateVoice = async (audioBlob, content) => {
    try {
      // 验证输入参数
      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('录音数据为空，请重新录音');
      }
      
      if (!content || content.trim() === '') {
        throw new Error('评测文本为空');
      }
      
      // 检查音频大小（限制为10MB）
      if (audioBlob.size > 10 * 1024 * 1024) {
        throw new Error('录音文件过大，请录制较短的音频');
      }
      
      // 创建音频 URL 用于播放
      const audioUrl = URL.createObjectURL(audioBlob);
      setRecordedAudioUrl(audioUrl);
      recordedAudioUrlRef.current = audioUrl; // 同时保存到 ref
      
      // 先播放用户录音，播放完成后再进行评测
      playRecordedAudio(audioUrl, audioBlob, content);
      
    } catch (error) {
      alert(error.message);
      
      // 清理录音 URL
      if (recordedAudioUrlRef.current) {
        URL.revokeObjectURL(recordedAudioUrlRef.current);
        setRecordedAudioUrl(null);
        recordedAudioUrlRef.current = null;
      }
    }
  };
  
  // 实际的评测 API 调用
  const performEvaluation = async (audioBlob, content) => {
    setIsEvaluating(true);
    setEvaluationResult(null);
    
    try {
      // 验证输入参数
      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('录音数据为空，请重新录音');
      }
      
      if (!content || content.trim() === '') {
        throw new Error('评测文本为空');
      }
      
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.mp3');
      formData.append('content', content);
      formData.append('eval_type', 2); // 英文句子评测
      formData.append('user_id', 'user12345'); // 可以从用户状态获取
      formData.append('audio_type', 1); // MP3格式
      
      // 设置请求超时
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30秒超时
      
      const result = await voiceAPI.evaluate(formData, controller.signal);
      clearTimeout(timeoutId);
      
      console.log('=== 课文领读评测API完整返回 ===');
      console.log('result:', JSON.stringify(result, null, 2));
      console.log('result.code:', result.code);
      console.log('result.data:', result.data);
      console.log('result.data.score:', result.data?.score);
      console.log('score类型:', typeof result.data?.score);
      console.log('================================');
      
      if (result.code === 200) {
        if (result.data && typeof result.data.score === 'number') {
          setEvaluationResult(result.data);
          console.log('评测结果:', result.data);
          
          // 根据分数显示对应的图片
          const score = result.data.score;
          let imageType = '';
          if (score < 60) {
            imageType = '测评差';
          } else if (score >= 60 && score < 80) {
            imageType = '测评中';
          } else if (score >= 80) {
            imageType = '测评好';
          }
          
          setScoreImageType(imageType);
          setShowScoreImage(true);
          
          // 2秒后隐藏图片
          setTimeout(() => {
            setShowScoreImage(false);
          }, 1500);
        } else {
          throw new Error('评测结果格式异常');
        }
      } else {
        throw new Error(result.message || '评测失败');
      }
      
    } catch (error) {
      console.error('语音评测失败:', error);
      
      let errorMessage = '语音评测失败';
      if (error.name === 'AbortError') {
        errorMessage = '评测请求超时，请检查网络连接';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = '网络连接失败，请检查评测服务是否启动';
      } else {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
      setEvaluationResult({
        error: true,
        message: errorMessage
      });
    } finally {
      setIsEvaluating(false);
      setCurrentEvaluatingSentence(-1);
      
      // 清理录音 URL
      if (recordedAudioUrlRef.current) {
        URL.revokeObjectURL(recordedAudioUrlRef.current);
        setRecordedAudioUrl(null);
        recordedAudioUrlRef.current = null;
      }
    }
  };

  // 播放录音
  const playRecordedAudio = async (audioUrl, audioBlob = null, content = null) => {
    try {
      const audio = new Audio(audioUrl);
      const currentAudioRef = { current: audio };
      setCurrentAudio(audio);
      setIsPlaying(true);
      
      // 设置播放结束回调
      audio.onended = () => {
        setCurrentAudio(null);
        setIsPlaying(false);
        
        // 播放完成后，如果有 audioBlob 和 content，则进行评测
        if (audioBlob && content) {
          performEvaluation(audioBlob, content);
        } else {
          // 如果没有评测数据，直接清理
          if (recordedAudioUrlRef.current) {
            URL.revokeObjectURL(recordedAudioUrlRef.current);
            setRecordedAudioUrl(null);
            recordedAudioUrlRef.current = null;
          }
        }
      };
      
      audio.onerror = (e) => {
        setCurrentAudio(null);
        setIsPlaying(false);
        
        console.error('录音播放失败:', e);
        alert('播放录音失败，请重试');
        
        // 清理录音 URL
        if (recordedAudioUrlRef.current) {
          URL.revokeObjectURL(recordedAudioUrlRef.current);
          setRecordedAudioUrl(null);
          recordedAudioUrlRef.current = null;
        }
      };
      
      await audio.play();
    } catch (error) {
      console.error('播放录音失败:', error);
      alert('播放录音失败：' + error.message);
      
      setCurrentAudio(null);
      setIsPlaying(false);
      
      // 清理录音 URL
      if (recordedAudioUrlRef.current) {
        URL.revokeObjectURL(recordedAudioUrlRef.current);
        setRecordedAudioUrl(null);
        recordedAudioUrlRef.current = null;
      }
    }
  };
  
  // 句子测评按钮点击处理
  const handleSentenceEvaluationClick = (sentenceIndex) => {
    if (isRecording) {
      // 如果正在录音，停止录音
      stopRecording();
    } else {
      // 如果没有录音，开始录音
      setCurrentEvaluatingSentence(sentenceIndex);
      startRecording();
    }
  };
  
  // 设置页面特殊标识
  useEffect(() => {
    document.body.classList.add('text-reading-page');
    
    return () => {
      document.body.classList.remove('text-reading-page');
      
      // 组件卸载时停止音频播放
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
      
      // 停止自动播放
      isAutoPlayingRef.current = false;
      isPlayingRef.current = false;
      
      // 清除所有定时器
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
      
      // 取消音频播放
      setAudioPlaybackCancelled(true);
      
      // 组件卸载时清除 sessionStorage 中的领读数据和访问标记
      sessionStorage.removeItem('textReadingData');
      sessionStorage.removeItem('textReadingPageVisited');
    };
  }, []); // 移除 currentAudio 依赖，只在组件挂载/卸载时执行

  // 同步设置到 ref
  useEffect(() => {
    readingSettingsRef.current = readingSettings;
  }, [readingSettings]);

  // 监听文章切换，重置句子索引并滚动到第一句
  useEffect(() => {
    // 只有在文章真正切换时才执行（不是初始化）
    if (articles.length > 0 && prevArticleIndexRef.current !== currentArticleIndex) {
      console.log('文章切换: 从', prevArticleIndexRef.current, '到', currentArticleIndex);
      
      // 更新追踪的索引
      prevArticleIndexRef.current = currentArticleIndex;
      
      // 确保句子索引重置为 0
      setCurrentSentenceIndex(0);
      
      // 延迟执行以确保 DOM 已更新
      const timer = setTimeout(() => {
        const sentenceElement = document.querySelector(`[data-sentence-index="0"]`);
        if (sentenceElement) {
          console.log('滚动到第一句');
          sentenceElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [currentArticleIndex, articles.length]);

  // 从 sessionStorage 获取选中的文章数据和设置
  useEffect(() => {
    const initializeReading = async () => {
      try {
        const sessionData = sessionStorage.getItem('textReadingData');
        let parsedData = null;
        
        const isPageRefresh = sessionStorage.getItem('textReadingPageVisited') === 'true';
        
        if (sessionData) {
          parsedData = JSON.parse(sessionData);
        }
        
        if (parsedData) {
          const articlesData = parsedData.articles || [];
          const settings = parsedData.settings;
          
          if (articlesData.length > 0) {
            setSelectedArticles(articlesData);
            
            // 按文章分组句子
            const groupedArticles = [];
            articlesData.forEach(article => {
              if (article.sentences && article.sentences.length > 0) {
                const articleSentences = article.sentences
                  .filter(sentence => sentence.audio && sentence.audio.res)
                  .map(sentence => ({
                    ...sentence,
                    articleTitle: article.title,
                    articleId: article.article_id,
                    articleClassify: article.classify
                  }));
                
                if (articleSentences.length > 0) {
                  groupedArticles.push({
                    articleId: article.article_id,
                    articleTitle: article.title,
                    classify: article.classify,
                    sentences: articleSentences
                  });
                }
              }
            });
            
            setArticles(groupedArticles);
            
            // 应用传入的设置（如果有的话）
            if (settings) {
              setReadingSettings(settings);
              readingSettingsRef.current = settings; // 立即更新 ref
            }
            
            // 标记页面已访问
            if (!isPageRefresh) {
              sessionStorage.setItem('textReadingPageVisited', 'true');
            }
          } else {
            setError('未找到课文数据');
          }
        } else {
          setError('未找到课文领读数据');
        }
      } catch (err) {
        console.error('解析课文领读数据失败:', err);
        setError('课文领读数据格式错误');
      } finally {
        setLoading(false);
      }
    };
    
    initializeReading();
  }, []);


  // 获取当前文章的所有唯一角色并分配位置（左/右）
  const getRolePositionMap = (articleId) => {
    // 找到当前文章的所有句子
    const currentArticle = articles.find(a => a.articleId === articleId);
    if (!currentArticle) return {};
    
    const articleSentences = currentArticle.sentences;
    
    // 提取所有唯一的 role_id
    const uniqueRoleIds = [...new Set(
      articleSentences
        .filter(s => s.role_id !== null && s.role_id !== undefined)
        .map(s => s.role_id)
    )];
    
    // 为每个角色分配位置：奇数索引在左，偶数索引在右
    const rolePositionMap = {};
    uniqueRoleIds.forEach((roleId, index) => {
      rolePositionMap[roleId] = index % 2 === 0 ? 'left' : 'right';
    });
    
    return rolePositionMap;
  };

  // 获取当前文章的句子总数
  const getCurrentArticleSentenceCount = useCallback(() => {
    if (articles.length > 0 && articles[currentArticleIndex]) {
      return articles[currentArticleIndex].sentences.length;
    }
    return 0;
  }, [articles, currentArticleIndex]);

  // 句子导航函数
  const previousSentence = () => {
    // 如果正在录音，先停止录音
    if (isRecording) {
      stopRecording();
      return; // 停止录音后不执行导航功能
    }
    
    // 停止自动播放和当前播放
    setIsAutoPlaying(false);
    isAutoPlayingRef.current = false;
    
    // 清除倒计时
    clearCountdown();
    
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
    
    if (isPlaying) {
      stopCurrentAudio();
    }
    
    if (currentSentenceIndex > 0) {
      // 还有上一句，继续在当前文章内导航
      const newIndex = currentSentenceIndex - 1;
      setCurrentSentenceIndex(newIndex);
      scrollToCurrentSentence(newIndex);
    } else if (currentArticleIndex > 0) {
      // 当前文章已是第一句，但还有上一篇文章，跳转到上一篇的第一句
      const prevArticleIndex = currentArticleIndex - 1;
      setCurrentArticleIndex(prevArticleIndex);
      setCurrentSentenceIndex(0); // 跳转到上一篇的第一句
      
      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // 如果既是第一句又是第一篇，则不做任何操作
  };

  const nextSentence = () => {
    // 如果正在录音，先停止录音
    if (isRecording) {
      stopRecording();
      return; // 停止录音后不执行导航功能
    }
    
    // 停止自动播放和当前播放
    setIsAutoPlaying(false);
    isAutoPlayingRef.current = false;
    
    // 清除倒计时
    clearCountdown();
    
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
    
    if (isPlaying) {
      stopCurrentAudio();
    }
    
    const totalSentences = getCurrentArticleSentenceCount();
    if (currentSentenceIndex < totalSentences - 1) {
      // 还有下一句，继续在当前文章内导航
      const newIndex = currentSentenceIndex + 1;
      setCurrentSentenceIndex(newIndex);
      scrollToCurrentSentence(newIndex);
    } else if (currentArticleIndex < articles.length - 1) {
      // 当前文章已是最后一句，但还有下一篇文章，跳转到下一篇
      const nextArticleIndex = currentArticleIndex + 1;
      setCurrentArticleIndex(nextArticleIndex);
      setCurrentSentenceIndex(0); // 重置到第一句
      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // 如果既是最后一句又是最后一篇，则不做任何操作
  };

  // 判断是否是当前文章的第一句
  const isFirstSentenceInArticle = () => {
    return currentSentenceIndex === 0;
  };

  // 判断是否还有上一篇文章
  const hasPreviousArticle = () => {
    return currentArticleIndex > 0;
  };

  // 判断是否是当前文章的最后一句
  const isLastSentenceInArticle = () => {
    const totalSentences = getCurrentArticleSentenceCount();
    return currentSentenceIndex === totalSentences - 1;
  };

  // 判断是否还有下一篇文章
  const hasNextArticle = () => {
    return currentArticleIndex < articles.length - 1;
  };

  // 滚动到当前句子
  const scrollToCurrentSentence = useCallback((sentenceIndex) => {
    setTimeout(() => {
      const sentenceElement = document.querySelector(`[data-sentence-index="${sentenceIndex}"]`);
      if (sentenceElement) {
        sentenceElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 150);
  }, []);

  // 获取当前句子
  const getCurrentSentence = () => {
    const currentArticle = articles[currentArticleIndex];
    if (currentArticle && currentArticle.sentences) {
      const sentence = currentArticle.sentences[currentSentenceIndex] || null;
      return sentence;
    }
    return null;
  };

  // 自动播放下一句
  const autoPlayNext = useCallback(() => {
    const totalSentences = getCurrentArticleSentenceCount();
    
    if (currentSentenceIndex < totalSentences - 1) {
      // 还有下一句，继续在当前文章内导航
      const newSentenceIndex = currentSentenceIndex + 1;
      setCurrentSentenceIndex(newSentenceIndex);
      scrollToCurrentSentence(newSentenceIndex);
      
      // 使用 setTimeout 来确保状态更新后再播放音频
      setTimeout(() => {
        if (isAutoPlayingRef.current) {
          const currentArticle = articles[currentArticleIndex];
          if (currentArticle && currentArticle.sentences && currentArticle.sentences[newSentenceIndex]) {
            const nextSentence = currentArticle.sentences[newSentenceIndex];
            playSentenceAudioRef.current(nextSentence);
          }
        }
      }, 150);
    } else if (currentArticleIndex < articles.length - 1) {
      // 当前文章已是最后一句，但还有下一篇文章，跳转到下一篇
      const nextArticleIndex = currentArticleIndex + 1;
      setCurrentArticleIndex(nextArticleIndex);
      setCurrentSentenceIndex(0);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      setTimeout(() => {
        if (isAutoPlayingRef.current) {
          const nextArticle = articles[nextArticleIndex];
          if (nextArticle && nextArticle.sentences && nextArticle.sentences[0]) {
            const firstSentence = nextArticle.sentences[0];
            playSentenceAudioRef.current(firstSentence);
          }
        }
      }, 300);
    } else {
      // 已经是最后一句，停止自动播放
      setIsAutoPlaying(false);
      isAutoPlayingRef.current = false;
      // 重置播放状态
      isPlayingRef.current = false;
      setIsPlaying(false);
    }
  }, [currentSentenceIndex, currentArticleIndex, articles, getCurrentArticleSentenceCount, scrollToCurrentSentence]);

  // 播放句子音频（包含跟读次数和设置）
  const playSentenceAudio = useCallback(async (sentence, customSettings = null) => {
    const currentPlaybackId = ++playbackIdRef.current;
    setAudioPlaybackCancelled(false);
    
    const currentSettings = customSettings || readingSettingsRef.current;
    
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    
    if (!sentence || !sentence.audio || !sentence.audio.res) {
      return;
    }
    
    if (currentPlaybackId !== playbackIdRef.current) {
      return;
    }
    
    if (!isPlayingRef.current) {
      isPlayingRef.current = true;
      setIsPlaying(true);
    }
    
    try {
      const audioUrl = sentence.audio.res;
      const playCount = currentSettings.playCount;
      const repeatCount = currentSettings.repeatCount;
      const pauseTimeMultiplier = currentSettings.pauseTime;
      
      const testResponse = await fetch(audioUrl, { method: 'HEAD' });
      if (!testResponse.ok) {
        throw new Error(`音频文件不可访问: ${testResponse.status}`);
      }
      
      const audio = new Audio(audioUrl);
      setCurrentAudio(audio);
      
      const playbackRate = currentSettings.playSpeed === 'fast' ? 1.5 : 
                          currentSettings.playSpeed === 'slow' ? 0.75 : 1;
      audio.playbackRate = playbackRate;
      
      await new Promise((resolve, reject) => {
        audio.onloadedmetadata = () => {
          audio.playbackRate = playbackRate;
          resolve();
        };
        audio.onerror = reject;
        audio.load();
      });
      
      const audioDuration = audio.duration;
      const pauseTime = audioDuration * pauseTimeMultiplier * 1000;

      // 根据跟读次数模式选择不同的播放逻辑
      if (repeatCount === 'playOneReadOne') {
        // 播一次读一次：句子-留白-句子-留白-句子-留白
        for (let i = 0; i < playCount; i++) {
          // 只检查 playbackId，避免异步状态更新问题
          if (currentPlaybackId !== playbackIdRef.current) {
            break;
          }
          
          // 播放句子
          await new Promise((resolve, reject) => {
            audio.currentTime = 0;
            
            const onEnded = () => {
              audio.removeEventListener('ended', onEnded);
              audio.removeEventListener('error', onError);
              resolve();
            };
            
            const onError = (error) => {
              audio.removeEventListener('ended', onEnded);
              audio.removeEventListener('error', onError);
              reject(error);
            };
            
            audio.addEventListener('ended', onEnded);
            audio.addEventListener('error', onError);
            
            audio.playbackRate = playbackRate;
            audio.play().catch(reject);
          });
          
          // 每次播放后都加留白
          if (pauseTime > 0 && currentPlaybackId === playbackIdRef.current) {
            // 在每次留白期间都显示倒计时
            const countdownSeconds = Math.ceil(pauseTime / 1000);
            startCountdown(countdownSeconds);
            
            await new Promise(resolve => {
              const timeout = setTimeout(resolve, pauseTime);
              const checkCancel = () => {
                if (currentPlaybackId !== playbackIdRef.current) {
                  clearTimeout(timeout);
                  resolve();
                } else {
                  setTimeout(checkCancel, 100);
                }
              };
              checkCancel();
            });
          }
        }
      } else {
        // 播完读一次：句子-句子-句子-留白
        for (let i = 0; i < playCount; i++) {
          // 只检查 playbackId，避免异步状态更新问题
          if (currentPlaybackId !== playbackIdRef.current) {
            break;
          }
          
          await new Promise((resolve, reject) => {
            audio.currentTime = 0;
            
            const onEnded = () => {
              audio.removeEventListener('ended', onEnded);
              audio.removeEventListener('error', onError);
              resolve();
            };
            
            const onError = (error) => {
              audio.removeEventListener('ended', onEnded);
              audio.removeEventListener('error', onError);
              reject(error);
            };
            
            audio.addEventListener('ended', onEnded);
            audio.addEventListener('error', onError);
            
            audio.playbackRate = playbackRate;
            audio.play().catch(reject);
          });
        }
        
        // 播完所有次数后加留白
        if (pauseTime > 0 && currentPlaybackId === playbackIdRef.current) {
          // 在留白期间显示倒计时
          const countdownSeconds = Math.ceil(pauseTime / 1000);
          startCountdown(countdownSeconds);
          
          await new Promise(resolve => {
            const timeout = setTimeout(() => {
              resolve();
            }, pauseTime);
            
            const checkCancel = () => {
              if (currentPlaybackId !== playbackIdRef.current) {
                clearTimeout(timeout);
                resolve();
                return;
              }
              setTimeout(checkCancel, 100);
            };
            checkCancel();
          });
        }
      }
      
    } catch (error) {
      console.error('音频播放失败:', error);
      // 播放失败时重置播放状态
      if (currentPlaybackId === playbackIdRef.current) {
        isPlayingRef.current = false;
        setIsPlaying(false);
        setCurrentAudio(null);
        setCountdown(0); // 清除倒计时
      }
    } finally {
      if (currentPlaybackId === playbackIdRef.current) {
        setCurrentAudio(null);
        setAudioPlaybackCancelled(false);
        setCountdown(0); // 清除倒计时/播放状态显示
        
        // 如果是自动播放模式，播放完成后自动进入下一句
        if (isAutoPlayingRef.current) {
          // 在自动播放模式下，不立即重置播放状态，保持连续播放的视觉效果
          setTimeout(() => {
            if (isAutoPlayingRef.current) {
              autoPlayNext();
            }
          }, 200);
        } else {
          // 非自动播放模式下，重置播放状态
          isPlayingRef.current = false;
          setIsPlaying(false);
        }
      }
    }
  }, [currentAudio, audioPlaybackCancelled, autoPlayNext]);

  // 创建 ref 来打破循环依赖
  const playSentenceAudioRef = useRef(playSentenceAudio);
  playSentenceAudioRef.current = playSentenceAudio;

  // 停止当前音频播放
  const stopCurrentAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }
    setAudioPlaybackCancelled(true);
    setCurrentAudio(null);
    // 重置播放状态
    isPlayingRef.current = false;
    setIsPlaying(false);
  };

  // 清除倒计时
  const clearCountdown = () => {
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(0);
  };

  // 开始倒计时（只用于显示，不额外增加等待时间）
  const startCountdown = (seconds) => {
    // 清除之前的倒计时
    clearCountdown();
    
    // 设置初始倒计时
    setCountdown(seconds);
    
    const runCountdown = (remaining) => {
      if (remaining > 0) {
        countdownTimerRef.current = setTimeout(() => {
          setCountdown(remaining - 1);
          runCountdown(remaining - 1);
        }, 1000);
      } else {
        // 倒计时结束
        setCountdown(0);
      }
    };
    
    runCountdown(seconds);
  };

  // 切换自动播放
  const toggleAutoPlay = () => {
    // 如果正在录音，先停止录音
    if (isRecording) {
      stopRecording();
      return; // 停止录音后不执行自动播放切换功能
    }
    
    const newAutoPlayState = !isAutoPlaying;
    setIsAutoPlaying(newAutoPlayState);
    isAutoPlayingRef.current = newAutoPlayState;
    
    // 如果关闭自动播放，清除倒计时
    if (!newAutoPlayState) {
      clearCountdown();
    }
    
    if (newAutoPlayState && !isPlaying) {
      const currentSentence = getCurrentSentence();
      if (currentSentence) {
        playSentenceAudio(currentSentence);
      }
    } else if (!newAutoPlayState) {
      // 停止自动播放时，也停止当前音频
      if (isPlaying) {
        stopCurrentAudio();
      }
    }
  };

  // 点击句子跳转
  const handleSentenceClick = (sentenceIndex) => {
    // 如果正在录音，先停止录音
    if (isRecording) {
      stopRecording();
      return; // 停止录音后不执行跳转功能
    }
    
    setCurrentSentenceIndex(sentenceIndex);
    scrollToCurrentSentence(sentenceIndex);
  };

  // 处理句子播放按钮点击
  const handleSentencePlayClick = (sentenceIndex) => {
    // 如果正在录音，先停止录音
    if (isRecording) {
      stopRecording();
      return; // 停止录音后不执行播放功能
    }
    
    // 如果正在自动播放，先停止自动播放
    if (isAutoPlaying) {
      setIsAutoPlaying(false);
      isAutoPlayingRef.current = false;
    }
    
    // 停止当前音频播放
    stopCurrentAudio();
    
    // 设置当前句子并播放
    setCurrentSentenceIndex(sentenceIndex);
    scrollToCurrentSentence(sentenceIndex);
    
    // 获取要播放的句子对象
    const currentArticle = articles[currentArticleIndex];
    const sentenceToPlay = currentArticle?.sentences?.[sentenceIndex];
    
    if (sentenceToPlay) {
      playSentenceAudio(sentenceToPlay);
    }
  };

  // 文章导航函数
  const previousArticle = () => {
    // 如果正在录音，先停止录音
    if (isRecording) {
      stopRecording();
      return; // 停止录音后不执行导航功能
    }
    
    if (currentArticleIndex > 0) {
      console.log('点击上一篇，当前文章索引:', currentArticleIndex, '当前句子索引:', currentSentenceIndex);
      
      // 停止当前播放
      if (isPlaying) {
        stopCurrentAudio();
      }
      
      // 立即重置句子索引为 0
      setCurrentSentenceIndex(0);
      
      // 然后切换文章索引
      setCurrentArticleIndex(prev => {
        const newIndex = prev - 1;
        console.log('切换到文章索引:', newIndex);
        return newIndex;
      });
      
      // 如果是自动播放模式，播放新文章的第一句
      if (isAutoPlayingRef.current) {
        setTimeout(() => {
          const prevArticle = articles[currentArticleIndex - 1];
          if (prevArticle && prevArticle.sentences && prevArticle.sentences[0]) {
            playSentenceAudio(prevArticle.sentences[0]);
          }
        }, 400);
      }
    }
  };

  const nextArticle = () => {
    // 如果正在录音，先停止录音
    if (isRecording) {
      stopRecording();
      return; // 停止录音后不执行导航功能
    }
    
    if (currentArticleIndex < articles.length - 1) {
      console.log('点击下一篇，当前文章索引:', currentArticleIndex, '当前句子索引:', currentSentenceIndex);
      
      // 停止当前播放
      if (isPlaying) {
        stopCurrentAudio();
      }
      
      // 立即重置句子索引为 0
      setCurrentSentenceIndex(0);
      
      // 然后切换文章索引
      setCurrentArticleIndex(prev => {
        const newIndex = prev + 1;
        console.log('切换到文章索引:', newIndex);
        return newIndex;
      });
      
      // 如果是自动播放模式，播放新文章的第一句
      if (isAutoPlayingRef.current) {
        setTimeout(() => {
          const nextArticleData = articles[currentArticleIndex + 1];
          if (nextArticleData && nextArticleData.sentences && nextArticleData.sentences[0]) {
            playSentenceAudio(nextArticleData.sentences[0]);
          }
        }, 400);
      }
    }
  };

  // 切换到指定文章
  const switchToArticle = (articleId) => {
    // 如果正在录音，先停止录音
    if (isRecording) {
      stopRecording();
      return; // 停止录音后不执行导航功能
    }
    
    const articleIndex = articles.findIndex(article => article.articleId === articleId);
    if (articleIndex !== -1 && articleIndex !== currentArticleIndex) {
      console.log('切换到指定文章，目标文章索引:', articleIndex);
      
      // 停止当前播放和自动播放
      if (isPlaying) {
        stopCurrentAudio();
      }
      
      // 停止自动播放
      setIsAutoPlaying(false);
      isAutoPlayingRef.current = false;
      
      // 清除倒计时
      clearCountdown();
      
      // 清除导航定时器
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
      
      // 立即重置句子索引为 0
      setCurrentSentenceIndex(0);
      
      // 然后切换到指定文章
      setCurrentArticleIndex(articleIndex);
      
      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  // 切换翻译显示
  const handleToggleTranslation = () => {
    // 如果正在录音，先停止录音
    if (isRecording) {
      stopRecording();
      return; // 停止录音后不执行切换功能
    }
    
    setShowTranslation(prev => !prev);
  };
  
  // 返回课文选择页面
  const handleBack = () => {
    // 如果正在录音，先停止录音
    if (isRecording) {
      stopRecording();
      return; // 停止录音后不执行导航功能
    }
    
    navigate('/text');
  };

  // 跳转到角色扮演页面
  const handleGoToRolePlay = () => {
    // 如果正在录音，先停止录音
    if (isRecording) {
      stopRecording();
      return; // 停止录音后不执行导航功能
    }
    
    // 获取当前文章
    const currentArticle = articles[currentArticleIndex];
    if (!currentArticle) {
      return;
    }
    
    // 准备角色扮演数据（只包含当前对话）
    const rolePlayData = {
      selectedDialogues: [
        {
          article_id: currentArticle.articleId,
          title: currentArticle.articleTitle,
          classify: currentArticle.classify,
          sentences: currentArticle.sentences
        }
      ]
    };
    
    // 将数据存储到 sessionStorage
    sessionStorage.setItem('rolePlayData', JSON.stringify(rolePlayData));
    
    // 跳转到角色扮演页面
    navigate('/role-play');
  };

  // 打开领读设置弹窗
  const handleOpenReadingSettings = () => {
    // 如果正在录音，先停止录音
    if (isRecording) {
      stopRecording();
      return; // 停止录音后不执行设置功能
    }
    
    setShowReadingSettings(true);
  };

  // 关闭领读设置弹窗
  const handleReadingSettingsClose = () => {
    setShowReadingSettings(false);
  };

  // 更新领读设置
  const handleReadingSettingsUpdate = (newSettings) => {
    setReadingSettings(newSettings);
    readingSettingsRef.current = newSettings;
    setShowReadingSettings(false);
  };

  // 播放速度变更处理
  const handleSpeedChange = (speed) => {
    // 如果正在录音，先停止录音
    if (isRecording) {
      stopRecording();
      return; // 停止录音后不执行速度变更功能
    }
    
    setReadingSettings(prev => ({
      ...prev,
      playSpeed: speed
    }));
    readingSettingsRef.current = {
      ...readingSettingsRef.current,
      playSpeed: speed
    };
  };


  // 渲染加载状态
  if (loading) {
    return (
      <div className="text-reading-container">
        <div className="text-reading-loading">
          <div className="loading-spinner"></div>
          <p>正在准备课文领读...</p>
        </div>
      </div>
    );
  }

  // 渲染错误状态
  if (error) {
    return (
      <div className="text-reading-container">
        <div className="text-reading-error">
          <p>{error}</p>
          <button className="back-button" onClick={handleBack}>
            返回课文选择
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="text-reading-container">
      {/* Logo区域 */}
      <div className="text-reading-header">
        <img 
          src={logoImage} 
          alt="大鱼人机口语" 
          className="text-reading-logo"
        />
      </div>
      
      {/* 左侧区域 */}
      <div className="text-reading-left">
        {/* 第一部分 - 句子显示区（可滚动） */}
        <div className={`text-reading-section-1 ${isAutoPlaying ? 'playing' : ''}`}>
          {/* 顶部蓝色半圆文字 */}
          <div className="top-semicircle-text">
            {isAutoPlaying ? (countdown > 0 ? `${countdown}s` : '播放') : '暂停'}
          </div>
          <div className="text-reading-content-scrollable">
            {/* 只显示当前文章 */}
            {articles.length > 0 && articles[currentArticleIndex] && (() => {
              const currentArticle = articles[currentArticleIndex];
              const isDialogue = currentArticle.classify === 1;
              const rolePositionMap = isDialogue ? getRolePositionMap(currentArticle.articleId) : {};
              
              return (
                <div key={currentArticle.articleId} className="article-section">
                  {/* 文章/对话标题 */}
                  <div className="text-reading-title-header">
                    <h2 className="text-reading-title">{currentArticle.articleTitle}</h2>
                  </div>
                  
                  {/* 句子列表 */}
                  {isDialogue ? (
                    // 对话模式
                    <div className="text-reading-dialogue-display">
                      {currentArticle.sentences.map((sentence, sentenceIndex) => {
                        // 头像根据角色分左右
                        const position = sentence.role_id ? rolePositionMap[sentence.role_id] : 'left';
                        const isCurrentSentence = sentenceIndex === currentSentenceIndex;
                        
                        return (
                          <div 
                            key={sentenceIndex} 
                            className={`dialogue-row dialogue-${position} ${isCurrentSentence ? 'current-sentence' : ''}`}
                            data-sentence-index={sentenceIndex}
                          >
                            {/* 头像 */}
                            <div className="dialogue-avatar">
                              {sentence.role_image && sentence.role_image.res ? (
                                <img src={sentence.role_image.res} alt={sentence.role_image.title || '角色'} />
                              ) : (
                                <div className="avatar-placeholder">👤</div>
                              )}
                            </div>
                            
                            {/* 对话气泡 */}
                            <div className="dialogue-bubble-container">
                              <div 
                                className={`dialogue-bubble ${isCurrentSentence && isPlaying ? 'playing' : ''}`}
                                onClick={() => handleSentenceClick(sentenceIndex)}
                                style={{ cursor: 'pointer' }}
                              >
                                {/* 统一布局顺序：文本内容在前，按钮在后 */}
                                <div className="dialogue-content">
                                  <div className="dialogue-sentence-english">
                                    {formatSentenceText(sentence.title)}
                                  </div>
                                  {showTranslation && sentence.en_title && (
                                    <div className="dialogue-sentence-translation">
                                      {sentence.en_title}
                                    </div>
                                  )}
                                </div>
                                
                                {/* 测评按钮 */}
                                <button 
                                  className={`sentence-evaluation-btn ${isRecording && currentEvaluatingSentence === sentenceIndex ? 'recording' : ''} ${isEvaluating && currentEvaluatingSentence === sentenceIndex ? 'evaluating' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSentenceEvaluationClick(sentenceIndex);
                                  }}
                                  title={isRecording && currentEvaluatingSentence === sentenceIndex ? "停止录音" : "句子测评"}
                                  disabled={isEvaluating && currentEvaluatingSentence === sentenceIndex}
                                >
                                  <img src={speakIcon} alt="说" className="evaluation-icon" />
                                  {isRecording && currentEvaluatingSentence === sentenceIndex && (
                                    <div className="status-text">正在录音...</div>
                                  )}
                                  {isEvaluating && currentEvaluatingSentence === sentenceIndex && (
                                    <div className="status-text">正在测评...</div>
                                  )}
                                </button>
                                
                                {/* 播放按钮 */}
                                <button 
                                  className={`sentence-play-btn ${isCurrentSentence && isPlaying ? 'playing' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSentencePlayClick(sentenceIndex);
                                  }}
                                  title={isCurrentSentence && isPlaying ? '停止播放' : '播放这句话'}
                                >
                                  <img src={listenIcon} alt="听" className="speaker-icon" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    // 文章模式
                    <div className="text-reading-article-display">
                      {currentArticle.sentences.map((sentence, sentenceIndex) => {
                        const isCurrentSentence = sentenceIndex === currentSentenceIndex;
                        
                        return (
                          <div 
                            key={sentenceIndex} 
                            className={`article-sentence-item ${isCurrentSentence ? 'current-sentence' : ''}`}
                            data-sentence-index={sentenceIndex}
                          >
                            <div 
                              className="sentence-content"
                              onClick={() => handleSentenceClick(sentenceIndex)}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="text-reading-sentence-english">
                                {formatSentenceText(sentence.title)}
                              </div>
                              {showTranslation && sentence.en_title && (
                                <div className="text-reading-sentence-translation">
                                  {sentence.en_title}
                                </div>
                              )}
                            </div>
                            
                            {/* 句子测评按钮 */}
                            <button 
                              className={`sentence-evaluation-btn ${isRecording && currentEvaluatingSentence === sentenceIndex ? 'recording' : ''} ${isEvaluating && currentEvaluatingSentence === sentenceIndex ? 'evaluating' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSentenceEvaluationClick(sentenceIndex);
                              }}
                              title={isRecording && currentEvaluatingSentence === sentenceIndex ? "停止录音" : "句子测评"}
                              disabled={isEvaluating && currentEvaluatingSentence === sentenceIndex}
                            >
                              <img src={speakIcon} alt="说" className="evaluation-icon" />
                                       {isRecording && currentEvaluatingSentence === sentenceIndex && (
                                         <div className="status-text">正在录音...</div>
                                       )}
                                       {isEvaluating && currentEvaluatingSentence === sentenceIndex && (
                                         <div className="status-text">正在测评...</div>
                                       )}
                            </button>
                            
                            {/* 句子播放按钮 */}
                            <button 
                              className={`sentence-play-btn ${isCurrentSentence && isPlaying ? 'playing' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSentencePlayClick(sentenceIndex);
                              }}
                              title={isCurrentSentence && isPlaying ? '停止播放' : '播放这句话'}
                            >
                              <img src={listenIcon} alt="听" className="speaker-icon" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
        
        {/* 第二部分 - 控制按钮区 */}
        <div className="text-reading-section-2">
          <div className="text-reading-controls">
            {/* 左侧控制按钮区 */}
            <div className="controls-left">
              <button 
                className="control-btn prev-sentence-btn"
                onClick={previousSentence}
                disabled={currentSentenceIndex === 0 && currentArticleIndex === 0}
                title={isFirstSentenceInArticle() && hasPreviousArticle() ? "上一篇" : "上一句"}
              >
                {isFirstSentenceInArticle() && hasPreviousArticle() ? "上一篇" : "上一句"}
              </button>
              
              <button 
                className={`control-btn auto-play-btn ${isAutoPlaying ? 'playing' : ''}`}
                onClick={toggleAutoPlay}
                title={isAutoPlaying ? '停止自动播放' : '开始自动播放'}
              >
                <div className="auto-play-icon">
                  {isAutoPlaying ? (
                    <>
                      <div className="pause-bar"></div>
                      <div className="pause-bar"></div>
                    </>
                  ) : (
                    <div className="play-triangle"></div>
                  )}
                </div>
              </button>
              
              <button 
                className="control-btn next-sentence-btn"
                onClick={nextSentence}
                disabled={currentSentenceIndex === getCurrentArticleSentenceCount() - 1 && currentArticleIndex === articles.length - 1}
                title={isLastSentenceInArticle() && hasNextArticle() ? "下一篇" : "下一句"}
              >
                {isLastSentenceInArticle() && hasNextArticle() ? "下一篇" : "下一句"}
              </button>
              
              <button 
                className="control-btn settings-btn"
                onClick={handleOpenReadingSettings}
                title="领读设置"
              >
                领读设置
              </button>
            </div>
            
            {/* 中间开关按钮区 */}
            <div className="toggle-switch-group">
              <label className="toggle-switch-label">
                <div 
                  className={`toggle-switch ${showTranslation ? 'active' : ''}`}
                  onClick={handleToggleTranslation}
                  title="显示/隐藏翻译"
                >
                  <div className="toggle-slider"></div>
                </div>
                <span className="toggle-label-text">翻译</span>
              </label>
              
              <div className="speed-selector-wrapper">
                <select 
                  className="speed-selector"
                  value={readingSettings.playSpeed}
                  onChange={(e) => handleSpeedChange(e.target.value)}
                  title="选择播放速度"
                >
                  <option value="slow">慢速</option>
                  <option value="normal">正常</option>
                  <option value="fast">快速</option>
                </select>
                <label className="speed-selector-label">播放速度</label>
              </div>
            </div>
            
            {/* 右侧功能按钮区 */}
            <div className="controls-right">
              {/* 角色扮演按钮 - 仅在当前文章是对话时显示 */}
              {articles.length > 0 && articles[currentArticleIndex] && articles[currentArticleIndex].classify === 1 && (
                <button 
                  className="control-btn back-btn"
                  onClick={handleGoToRolePlay}
                  title="进入角色扮演页面"
                >
                  角色扮演
                </button>
              )}
              <button 
                className="control-btn back-btn"
                onClick={handleBack}
                title="返回课文选择页面"
              >
                返回同步课文
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* 右侧区域 - 已选课文列表 */}
      <div className="text-reading-right">
        <div className="text-reading-articles-list">
          <div className="articles-list-header">
            <h3>已选课文 ({selectedArticles.length})</h3>
          </div>
          <div className="articles-list-content">
            {selectedArticles.map((article, index) => {
              // 检查当前文章是否是正在显示的文章
              const isCurrentArticle = articles.length > 0 && 
                articles[currentArticleIndex] && 
                articles[currentArticleIndex].articleId === article.article_id;
              
              return (
                <div 
                  key={article.article_id} 
                  className={`article-list-item ${isCurrentArticle ? 'active' : ''}`}
                  onClick={() => switchToArticle(article.article_id)}
                >
                  <div className="article-list-item-title">{article.title}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* 评测结果弹窗 */}
      {showScoreImage && (
        <div className="score-image-overlay" onClick={() => setShowScoreImage(false)}>
          <div className="score-image-container">
            <img 
              src={
                scoreImageType === '测评好' ? scoreGoodImage :
                scoreImageType === '测评差' ? scoreBadImage :
                scoreImageType === '测评中' ? scoringImage : ''
              } 
              alt={scoreImageType} 
              className="score-image"
            />
          </div>
        </div>
      )}
      
      {/* 领读设置弹窗 */}
      <SettingsModal
        isOpen={showReadingSettings}
        onClose={handleReadingSettingsClose}
        onStart={handleReadingSettingsUpdate}
        title={articleReadingConfig.title}
        settingItems={articleReadingConfig.settingItems}
        defaultSettings={readingSettings}
      />
    </div>
  );
};

export default TextReading;

